import React, { useState, useCallback } from "react";
import Select from "react-select";
import { NXAlertCircle } from "../icons";
import CollectionModal from "../pages/collection/modal";
import ProductTypeModal from "../pages/productType/modal";

interface Option {
  value: string;
  label: string;
}

interface SelectStyledProps {
  bg?: string;
  border?: boolean;
  accentColor?: string;
  name?: string;
  indicator?: boolean;
  options?: Option[];
  defaultValue?: Option | Option[];
  onChange?: (value: any, actionMeta: any) => void;
  limit?: number;
  isOptionDisabled?: (option: Option) => boolean;
  valueMax?: number;
  isMulti?: boolean;
  customStyles?: any;
  errorData?: any;
  isDisabled?: boolean;
  className?: string;
  addComponentName?: string;
  menuPortalTarget?: any;
  label?: string;
  helpText?: string;
  infoClassName?: string;
  labelProps?: Record<string, any>;
  required?: boolean;
}

const defaultOptions: Option[] = [
  { value: "chocolate", label: "Chocolate" },
  { value: "strawberry", label: "Strawberry" },
  { value: "vanilla", label: "Vanilla" },
];

const SelectStyled: React.FC<SelectStyledProps> = ({
  options = defaultOptions,
  defaultValue,
  onChange,
  limit,
  isOptionDisabled,
  valueMax = 3,
  isMulti,
  customStyles,
  errorData,
  name,
  isDisabled,
  className,
  addComponentName,
  menuPortalTarget,
  label,
  helpText,
  infoClassName,
  labelProps,
  required,
  ...rest
}) => {
  const [isValid, setIsValid] = useState(true);
  const [values, setValues] = useState<Option[]>([]);
  const [openDrawer, setOpenDrawer] = useState<string | null>(null);

  const errorMessages = name && errorData && errorData[name] ? errorData[name] : [];

  const onChangeWithLimit = useCallback(
    (value: any, actionMeta: any) => {
      if (onChange) {
        if (
          isMulti &&
          actionMeta.action === "select-option" &&
          valueMax &&
          Array.isArray(value) &&
          value.length > valueMax
        ) {
          setIsValid(false);
        } else {
          setIsValid(true);
          setValues(value);
          return onChange(value, actionMeta);
        }
      }
    },
    [limit, onChange, isMulti, valueMax]
  );

  const onAddButtonClick = () => {
    if (addComponentName) {
      setOpenDrawer(addComponentName);
    }
  };

  const onDrawerModalClose = () => {
    setOpenDrawer(null);
  };

  return (
    <div className="flex flex-col">
      {label && (
        <div className="flex items-center space-x-2 mb-1">
          <label htmlFor={name} {...labelProps} className="text-sm font-medium text-gray-700">
            {label}
          </label>
          <div className="relative group inline-block">
            {/* Help Icon */}
            <NXAlertCircle className={infoClassName ? infoClassName : 'text-base-300'} />
            {/* Tooltip */}
            {helpText && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity z-50">
                {helpText}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="flex items-center">
        <div className="flex-grow">
          {(values.length >= valueMax || !isValid) && (
            <small className="text-danger">
              You can't choose more than {valueMax} items
            </small>
          )}
          <Select
            defaultValue={defaultValue}
            isOptionDisabled={values.length ? () => values.length >= valueMax : undefined}
            options={options}
            instanceId="inId"
            onChange={onChangeWithLimit}
            required={required}
            isMulti={isMulti}
            styles={customStyles}
            isDisabled={isDisabled}
            className={className}
            menuPortalTarget={menuPortalTarget}
            {...rest}
          />
          {errorMessages.length > 0 && (
            <div className="text-red-600 text-sm mt-1">
              {errorMessages.map((error: any, index: number) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}
        </div>
        {addComponentName && (
          <button
            type="button"
            onClick={onAddButtonClick}
            className="ml-1 w-7 h-7 flex items-center justify-center text-black bg-white border border-gray-400 rounded-full transition-all duration-300 ease-in-out hover:bg-gray-200 hover:border-gray-300"
          >
            +
          </button>
        )}
      </div>

      {/* Render Drawer/Modal Based on State */}
       <CollectionModal isOpen={openDrawer === "collections"} onClose={onDrawerModalClose} onSubmit={() => {  }} />
       <ProductTypeModal isOpen={openDrawer === "producttype"} onClose={onDrawerModalClose} onSubmit={() => {  }} />
    </div>
  );
};

export default SelectStyled;
